import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { http, errMsg, kitPalette } from "../lib/api";
import { FillPagesButton } from "../components/kit/FillPagesButton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ContentTab } from "../components/kit/ContentTab";
import { VerificationTab } from "../components/kit/VerificationTab";
import { ProductTab } from "../components/kit/ProductTab";
import { PreviewTab } from "../components/kit/PreviewTab";
import { PdfFixTab } from "../components/kit/PdfFixTab";
import { FinishNowButton } from "../components/kit/FinishNowButton";
import { SafeBoundary } from "../components/kit/SafeBoundary";
import { InstagramChecklist } from "../components/kit/InstagramChecklist";
import { ProgressPanel } from "../components/kit/ProgressPanel";
import { PhotosTab } from "../components/kit/PhotosTab";
import { BeenTherePanel } from "../components/kit/BeenTherePanel";
import { FounderLinksPanel } from "../components/kit/FounderLinksPanel";
import { OriginalPanel } from "../components/kit/OriginalPanel";
import { PreviewButtons } from "../components/kit/PreviewButtons";
import { CompleteEntries } from "../components/kit/CompleteEntries";
import { ImportChanges } from "../components/kit/ImportChanges";
import { PageCheckTab } from "../components/kit/PageCheckTab";
import { KitHeader } from "../components/kit/layout/KitHeader";
import { AccuracyPanel } from "../components/kit/layout/AccuracyPanel";
import { IllustrationsPanel } from "../components/kit/layout/IllustrationsPanel";
import { InstagramPanel } from "../components/kit/layout/InstagramPanel";
import { DownloadCards } from "../components/kit/layout/DownloadCards";
import { SpeedPanel } from "../components/kit/SpeedPanel";
import { JobStops } from "../components/kit/JobStops";
import { StyleCheckPanel, HistoryPanel } from "../components/ops/KitOps";
import { VideoPrompts } from "../components/VideoPrompts";
import { RetryButton } from "../components/register/SpeedCard";
import { StopJobsButton } from "../components/kit/StopJobsButton";
import { RecheckBanner } from "../components/kit/RecheckBanner";
import { LiveActivity } from "../components/kit/LiveActivity";

const BUSY = ["queued", "generating", "verifying", "fact_checking", "fixing"];
const trig = "rounded-none bg-transparent px-4 py-3 text-sm text-stone-500 border-b-2 border-transparent -mb-px data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-[#2B2A26] hover:text-[#2B2A26] transition-colors";
const Count = ({ n, cls = "text-stone-400" }) => (n ? <span className={`ml-1.5 font-mono-ui text-[10px] ${cls}`}>{n}</span> : null);

const Toolbar = ({ kit, reload, busy, hasContent }) => {
  const regen = async () => {
    try { await http.post(`/kits/${kit.id}/regenerate`, {}); toast.success("Full regeneration started"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="kit-toolbar" className="flex flex-wrap gap-2 mb-8">
      <StopJobsButton kit={kit} reload={reload} />
      <PreviewButtons kitId={kit.id} disabled={!hasContent || busy} />
      {!busy && hasContent && <CompleteEntries kit={kit} reload={reload} />}
      {(busy || kit.filling || kit.completing) && hasContent && <FinishNowButton kitId={kit.id} reload={reload} />}
      {kit.status === "failed" && <RetryButton kitId={kit.id} reload={reload} />}
      <button data-testid="regenerate-kit-button" onClick={regen} disabled={busy} className="inline-flex items-center gap-2 rounded-sm border border-stone-300 px-4 py-2 text-sm text-stone-600 hover:text-stone-900 hover:bg-stone-100 transition-colors disabled:opacity-40">
        <RefreshCw size={15} /> Regenerate
      </button>
      <FillPagesButton kit={kit} reload={reload} disabled={!hasContent || busy} />
    </div>
  );
};

const ImportSummary = ({ kit, reload }) => (
  <div data-testid="kit-import-summary" className="border border-stone-200 bg-white p-4 mb-8 text-sm text-stone-600">
    <b className="text-[#2B2A26]">Imported from {kit.import.filename}</b>{kit.import.pages ? ` · ${kit.import.pages} pages` : ""}
    {kit.import.done ? <> · kept your wording in <b>{kit.import.kept?.length || 0}</b> sections · wrote <b>{kit.import.written?.length || 0}</b> missing{kit.import.written?.length ? ` (${kit.import.written.join(", ")})` : ""}{kit.import.unmapped?.length ? ` · ${kit.import.unmapped.length} passages didn’t fit a section` : ""}</> : kit.status === "failed" ? "" : " · converting…"}
    <ImportChanges imp={kit.import} kitId={kit.id} reload={reload} />
  </div>
);

export default function KitDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [kit, setKit] = useState(null);
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "content");
  const [jump, setJump] = useState(null);
  const [problems, setProblems] = useState(true);
  const load = useCallback(() => http.get(`/kits/${id}`).then((r) => setKit(r.data)).catch(() => nav("/")), [id, nav]);
  const busy = kit && BUSY.includes(kit.status);

  useEffect(() => { load(); }, [load]);
  const polling = busy || kit?.filling || kit?.page_checking || kit?.running_jobs?.length || kit?.publish_job?.state === "running" || kit?.recheck?.state === "checking" || kit?.carousel_building || kit?.completing;
  useEffect(() => {
    if (!polling) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [polling, load]);

  if (!kit) return <div className="p-12 eyebrow">Loading kit…</div>;
  const c = kitPalette(kit);
  const hasContent = kit.sections?.length > 0;
  const pdfOnly = ["standalone", "guide"].includes(kit.collection);
  const goTab = (t) => { setTab(t); setTimeout(() => document.querySelector('[data-testid="kit-tabs"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 80); };
  const openPage = (n) => { setJump({ n }); goTab("pdf"); };

  return (
    <div className="p-8 lg:p-12 max-w-[1400px]">
      <KitHeader kit={kit} c={c} busy={busy} reload={load} pdfOnly={pdfOnly} />
      <Toolbar kit={kit} reload={load} busy={busy} hasContent={hasContent} />
      <RecheckBanner kit={kit} />
      <LiveActivity kit={kit} />
      <SpeedPanel kit={kit} />
      <JobStops kit={kit} reload={load} />
      {hasContent && ["researched", "founders"].includes(kit.collection) && <StyleCheckPanel kit={kit} reload={load} />}
      <HistoryPanel kit={kit} reload={load} />
      {(busy || kit.status === "failed" || kit.error) && <ProgressPanel kit={kit} />}
      {kit.import && <ImportSummary kit={kit} reload={load} />}
      {kit.status === "selecting" && <BeenTherePanel kit={kit} reload={load} />}

      {hasContent && (
        <SafeBoundary key={kit.updated_at}>
          <AccuracyPanel kit={kit} c={c} reload={load} busy={busy} />
          <IllustrationsPanel kit={kit} c={c} reload={load} onOpen={() => goTab("photos")} />
          {!kit.bespoke && <InstagramPanel kit={kit} c={c} reload={load} />}
          <DownloadCards kit={kit} c={c} disabled={!hasContent || busy} pdfOnly={pdfOnly} />
          <Tabs data-testid="kit-tabs" value={tab} onValueChange={setTab} className="rise">
            <TabsList className="bg-transparent rounded-none h-auto p-0 border-b border-stone-200 w-full justify-start flex-wrap mb-8">
              <TabsTrigger className={trig} style={tab === "content" ? { borderColor: c.primary } : undefined} data-testid="tab-content" value="content">Chapters</TabsTrigger>
              <TabsTrigger className={trig} style={tab === "verification" ? { borderColor: c.primary } : undefined} data-testid="tab-verification" value="verification">Verification<Count n={kit.summary.blockers} cls="text-amber-600" /></TabsTrigger>
              <TabsTrigger className={trig} style={tab === "photos" ? { borderColor: c.primary } : undefined} data-testid="tab-photos" value="photos">Photography<Count n={kit.summary.images} /></TabsTrigger>
              <TabsTrigger className={trig} style={tab === "pagecheck" ? { borderColor: c.primary } : undefined} data-testid="tab-page-check" value="pagecheck">Page check<Count n={kit.summary.page_check?.fails} cls="text-amber-700" /></TabsTrigger>
              <TabsTrigger className={trig} style={tab === "pdf" ? { borderColor: c.primary } : undefined} data-testid="tab-pdf-preview" value="pdf">PDF preview</TabsTrigger>
              {!pdfOnly && <TabsTrigger className={trig} style={tab === "preview" ? { borderColor: c.primary } : undefined} data-testid="tab-preview" value="preview">App preview</TabsTrigger>}
              <TabsTrigger className={trig} style={tab === "product" ? { borderColor: c.primary } : undefined} data-testid="tab-product" value="product">Store listing</TabsTrigger>
              {kit.collection === "founders" && <TabsTrigger className={trig} style={tab === "founder" ? { borderColor: c.primary } : undefined} data-testid="tab-founder-visits" value="founder">Founder visits<Count n={(kit.sections || []).reduce((n, s) => n + (s.entries || []).filter((e) => e.founder_verified).length, 0)} cls="text-[#9A7B2C]" /></TabsTrigger>}
              <TabsTrigger className={trig} style={tab === "instagram" ? { borderColor: c.primary } : undefined} data-testid="tab-instagram" value="instagram">Instagram checklist</TabsTrigger>
              <TabsTrigger className={trig} style={tab === "video" ? { borderColor: c.primary } : undefined} data-testid="tab-video" value="video">Video prompts</TabsTrigger>
            </TabsList>
            <TabsContent value="content"><ContentTab kit={kit} reload={load} c={c} /></TabsContent>
            <TabsContent value="verification"><VerificationTab kit={kit} reload={load} /></TabsContent>
            <TabsContent value="photos"><PhotosTab kit={kit} reload={load} /></TabsContent>
            <TabsContent value="pagecheck"><PageCheckTab kit={kit} reload={load} disabled={busy} onOpen={openPage} problems={problems} setProblems={setProblems} /></TabsContent>
            <TabsContent value="pdf"><PdfFixTab kit={kit} reload={load} jump={jump} /></TabsContent>
            <TabsContent value="preview"><PreviewTab kit={kit} /></TabsContent>
            <TabsContent value="product"><ProductTab kit={kit} reload={load} /></TabsContent>
            <TabsContent value="founder">{kit.collection === "founders" && <OriginalPanel kit={kit} reload={load} />}{kit.collection === "founders" && !busy && <FounderLinksPanel key={kit.updated_at} kit={kit} reload={load} />}</TabsContent>
            <TabsContent value="instagram"><div data-testid="instagram-tab" className="max-w-3xl"><InstagramChecklist kit={kit} /></div></TabsContent>
            <TabsContent value="video"><div data-testid="video-tab" className="max-w-6xl"><VideoPrompts data={kit} path={`/kits/${kit.id}/video-prompts`} reload={load} /></div></TabsContent>
          </Tabs>
        </SafeBoundary>
      )}
    </div>
  );
}
